/* import __COLOCATED_TEMPLATE__ from './messenger.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type TabItem, type Tooltip } from '../common/standard-base';
import type MessengerInstallation from 'embercom/services/messenger-installation';
import { INSTALLATION_STATUS } from 'embercom/components/installation-new/constants';
import type Router from '@ember/routing/router-service';

const BASE_TABS: readonly TabItem[] = Object.freeze([
  {
    label: 'new-settings.channels.messenger.tabs.web',
    value: 'web',
  },
  {
    label: 'new-settings.channels.messenger.tabs.mobile-sdk',
    value: 'mobile-sdk',
  },
  {
    label: 'new-settings.channels.messenger.tabs.conversations',
    value: 'conversations',
  },
  {
    label: 'new-settings.channels.messenger.tabs.general',
    value: 'general',
  },
]);

const NUDGES_TAB: TabItem = {
  label: 'new-settings.channels.messenger.tabs.nudges',
  value: 'nudges',
};

const INSTALL_TAB: TabItem = {
  label: 'new-settings.channels.messenger.tabs.install',
  value: 'install',
};

const SECURITY_TAB: TabItem = {
  label: 'new-settings.channels.messenger.tabs.security',
  value: 'security',
};

interface Signature {
  Args: {
    selectedTab?: string;
    setSelectedTab: (value: string) => void;
    settings: any;
    identity: any;
  };
  Blocks: {
    default: Array<unknown>;
  };
}

export default class Messenger extends Component<Signature> {
  @service declare messengerInstallation: MessengerInstallation;
  @service declare appService: AppService;
  @service declare router: Router;

  get app() {
    return this.appService.app;
  }

  get groups(): readonly TabItem[] {
    let tabItems = [...BASE_TABS];

    if (this.app.canUseMessengerNudges) {
      tabItems.push(NUDGES_TAB);
    }

    let installTab = INSTALL_TAB;

    if (this.shouldShowIdvTooltip && !this.shouldShowSecurityTab) {
      let idvTooltip: Tooltip = {
        text: 'new-settings.channels.messenger.tabs.recommend-idv-tooltip',
        icon: 'alert-circle',
        iconColor: 'notice-fill',
      };

      installTab = { ...INSTALL_TAB, tooltip: idvTooltip };
    }

    let index = tabItems.findIndex((item) => item.value === installTab.value);
    if (index === -1) {
      tabItems.push(installTab);
    } else {
      tabItems[index] = installTab;
    }

    if (this.shouldShowSecurityTab) {
      let securityTab = SECURITY_TAB;

      if (this.shouldShowIdvTooltip) {
        let jwtTooltip: Tooltip = {
          text: 'new-settings.channels.messenger.tabs.recommend-jwt-tooltip',
          icon: 'alert-circle',
          iconColor: 'notice-fill',
        };

        securityTab = { ...SECURITY_TAB, tooltip: jwtTooltip };
      }

      tabItems.push(securityTab);
    }

    return tabItems;
  }

  get shouldShowSecurityTab() {
    return this.app.canUseJwtSecuritySettings;
  }

  get shouldShowIdvTooltip() {
    let installationStatuses = [
      this.messengerInstallation.webStatus,
      this.messengerInstallation.iosStatus,
      this.messengerInstallation.androidStatus,
    ];
    return installationStatuses.any(
      (status) => status === INSTALLATION_STATUS.INSECURELY_INSTALLED,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Messenger': typeof Messenger;
    'new-settings/channels/messenger': typeof Messenger;
  }
}
